// ProgramFormModal.tsx
import React, { useState, useEffect, useMemo } from "react";
import { Modal, Button, Form, Offcanvas } from "react-bootstrap";
import { toast } from "react-toastify";
import SelectGroup from "./SelectGroup";
import { useTranslate } from "../../hooks/LanguageProvider";
import { useClipboard } from "../../utilities/functions";
import ProgramTable from "./ProgramTable";
import { useParams } from "react-router-dom";
import ProgramTableModal from "./ProgramTableModal";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface ProgramFormModalProps {
  show: boolean;
  onHide: () => void;
  id_site: number | null;
  onSuccess?: () => void;
  editProgramData?: any;
}

interface ProgramFormData {
  program_date: string;
  id_client: string;
  id_departure: string;
  destination_ids: string[];
  type: string;
  id_conducteur: string;
  id_vehicle: string;
  observation: string;
  status: string;
}


interface ProgramInterface {
  id_program: number;
  id_site?: number;
  program_date?: string;
  site?: string;
  name?: string;
  client_name?: string;
  departure?: string;
  destination?: string;
  type?: string;
  observation?: string;
  status: string;
  nom_conducteur?: string;
  prenom_conducteur?: string;
  immatriculation_vehicule?: string;
  PSN?: string;
  num_porte_vehicule?: string;
}

const typeOptions = ["BBA", "C", "M", "CB", "PB", "MALAX", "7T", "10T", "5T", "2T5", "3T5", "1T5", "1,5T", "CLINIQUE", "CC", "P"];
const statusOptions = ["planned", "in_progress", "completed", "canceled"];

const ProgramFormModal: React.FC<ProgramFormModalProps> = ({
  show,
  onHide,
  id_site,
  onSuccess,
  editProgramData,
}) => {
  const { translate } = useTranslate();
  const { copyToClipboard, copiedId } = useClipboard("");

  const id_user = localStorage.getItem("userid");
  const geopuserID = localStorage.getItem("GeopUserID") || id_user;

  // ------------------------
  // FORM STATES
  // ------------------------
  const [formData, setFormData] = useState<ProgramFormData>({
    program_date: "",
    id_client: "",
    id_departure: "",
    destination_ids: [],
    type: "",
    id_conducteur: "",
    id_vehicle: "",
    observation: "",
    status: "",
  });

  const [drivers, setDrivers] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [destinations, setDestinations] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [isEditMode, setIsEditMode] = useState(false);
  const [editingProgramId, setEditingProgramId] = useState<number | null>(null);

  const optionsReady =
    drivers.length > 0 &&
    vehicles.length > 0 &&
    clients.length > 0 &&
    destinations.length > 0;

  // ------------------------
  // MODE EDIT
  // ------------------------
  const enterEditMode = (programId: number, programData: any) => {
    setIsEditMode(true);
    setEditingProgramId(programId);

    const departureId =
      destinations.find((dest) => dest.name === programData.departure)?.id_destination.toString() || "";

    const destinationIds: string[] = [];
    if (programData.destination) {
      const destNames = programData.destination.split(", ");
      destNames.forEach((name: string) => {
        const dest = destinations.find((d) => d.name === name.trim());
        if (dest) destinationIds.push(dest.id_destination.toString());
      });
    }

    setFormData({
      program_date: programData.program_date
        ? (() => {
          const d = new Date(programData.program_date);
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, "0");
          const day = String(d.getDate()).padStart(2, "0");
          return `${year}-${month}-${day}`;
        })()
        : "",
      id_client:
        programData.id_client !== undefined && programData.id_client !== null
          ? String(programData.id_client)
          : "",
      id_departure: departureId,
      destination_ids: destinationIds,
      type: programData.type || "",
      observation: programData.observation || "",
      status: programData.status || "planned",
      id_conducteur:
        programData.id_conducteur !== undefined && programData.id_conducteur !== null
          ? String(programData.id_conducteur)
          : "",
      id_vehicle:
        programData.id_vehicle !== undefined && programData.id_vehicle !== null
          ? String(programData.id_vehicle)
          : "",
    });
  };

  const exitEditMode = () => {
    setIsEditMode(false);
    setEditingProgramId(null);
    setFormData({
      program_date: "",
      id_client: "",
      id_departure: "",
      destination_ids: [],
      type: "",
      id_conducteur: "",
      id_vehicle: "",
      observation: "",
      status: "",
    });
    setSearchTerm("");
  };

  const handleModalHide = () => {
    exitEditMode();
    setSearchTerm("");
    onHide?.();
  };

  // ------------------------
  // FETCH OPTIONS
  // ------------------------
  useEffect(() => {
    if (!show) return;
    if (!backendUrl) return;

    const fetchDrivers = async () => {
      try {
        const url = geopuserID
          ? `${backendUrl}/api/program/drivers/${geopuserID}`
          : `${backendUrl}/api/program/drivers`;

        const response = await fetch(url);
        if (!response.ok) return;

        const data = await response.json();

        setDrivers(
          data
            .filter(
              (d: any) =>
                d.nom_conducteur?.trim() !== "" &&
                d.prenom_conducteur?.trim() !== ""
            )
            .map((driver: any) => ({
              id_conducteur: driver.id_conducteur,
              nom_conducteur: driver.nom_conducteur,
              prenom_conducteur: driver.prenom_conducteur,
            }))
        );
      } catch {
        setDrivers([]);
      }
    };

    const fetchVehicles = async () => {
      try {
        const url = geopuserID
          ? `${backendUrl}/api/program/vehicles/${geopuserID}`
          : `${backendUrl}/api/program/vehicles`;

        const res = await fetch(url);
        if (!res.ok) return;

        const json = await res.json();
        setVehicles(Array.isArray(json) ? json : []);
      } catch {
        setVehicles([]);
      }
    };

    const fetchClients = async () => {
      try {
        const url = geopuserID
          ? `${backendUrl}/api/program/clients/${geopuserID}`
          : `${backendUrl}/api/program/clients`;

        const res = await fetch(url);
        if (!res.ok) return;

        const json = await res.json();
        setClients(Array.isArray(json) ? json : []);
      } catch {
        setClients([]);
      }
    };

    const fetchDestinations = async () => {
      try {
        const res = await fetch(`${backendUrl}/api/destinations`);
        if (!res.ok) return;

        const json = await res.json();
        setDestinations(json);
      } catch {
        setDestinations([]);
      }
    };

    fetchDrivers();
    fetchVehicles();
    fetchClients();
    fetchDestinations();
  }, [show, backendUrl, geopuserID]);

  // ------------------------
  // APPLY EDIT DATA
  // ------------------------
  useEffect(() => {
    if (editProgramData && optionsReady) {
      enterEditMode(editProgramData.id_program, editProgramData);
    } else {
      exitEditMode();
    }
  }, [editProgramData, optionsReady]);

  // ------------------------
  // SUBMIT CREATE / UPDATE
  // ------------------------
  const handleCreateProgram = async () => {
    if (!formData.program_date || !formData.id_departure || !formData.type) {
      toast.error("Please fill out required fields.");
      return;
    }

    try {
      const programData = {
        program_date: formData.program_date,
        id_client: formData.id_client || null,
        id_site,
        id_departure: formData.id_departure,
        type: formData.type,
        id_conducteur: formData.id_conducteur || null,
        id_vehicle: formData.id_vehicle || null,
        observation: formData.observation,
        status: formData.status || "planned",
      };

      const payload = {
        programData: programData,
        destination_ids: formData.destination_ids,
      };

      const response = await fetch(`${backendUrl}/api/program/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Error adding program");

      toast.success("Program added successfully!");
      onSuccess?.();
      exitEditMode();
    } catch (error) {
      toast.error("Error creating program");
    }
  };

  const handleUpdateProgram = async () => {
    if (!formData.program_date || !formData.id_departure || !formData.type) {
      toast.error("Please fill out required fields.");
      return;
    }

    try {
      const payload = {
        program_date: formData.program_date,
        id_client: formData.id_client || null,
        id_site,
        id_departure: formData.id_departure,
        type: formData.type,
        id_conducteur: formData.id_conducteur || null,
        id_vehicle: formData.id_vehicle || null,
        observation: formData.observation,
        status: formData.status || "planned",
        destination_ids: formData.destination_ids,
      };

      const response = await fetch(`${backendUrl}/api/program/update/${editingProgramId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Error updating program");

      toast.success("Program updated successfully!");
      onSuccess?.();
      exitEditMode();
    } catch (error) {
      toast.error("Error updating program");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isEditMode && editingProgramId) {
      await handleUpdateProgram();
    } else {
      await handleCreateProgram();
    }
  };
  //list programs

  const [loading, setLoading] = useState(false);

  const siteId = Number(id_site);

  const [selectedColumns, setSelectedColumns] = useState<Record<string, boolean>>({
    id_program: true,
    program_date: true,
    departure: true,
    destination: true,
    type: true,
    observation: true,
    status: true,
    nom_conducteur: true,
    immatriculation_vehicule: true,
    client_name: true,
  });



  const [sort, setSort] = useState<"ASC" | "DESC">("DESC");
  const [column, setColumn] = useState("id_site");
  const [limit, setLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);



  const [total, setTotal] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [filterDate, setFilterDate] = useState<string | null>(null);


  const [type, setType] = useState(id_user === "1" ? 0 : 2);
  const [searchValue, setSearchValue] = useState("");



  // Fonction passée à l'enfant
  const handleTypeSearch = (val: string) => {
    const numero = parseInt(val, 10);
    setType(numero);
  };


  // ------ MODALS ------ //
  const [showDeleteProgramModal, setShowDeleteProgramModal] = useState(false);
  const [showShowProgramModal, setShowShowProgramModal] = useState(false);
  const [selectedProgramId, setSelectedProgramId] = useState<number | null>(null);

  const [showFormModal, setShowFormModal] = useState(false);
  const [editProgramsData, setEditProgramData] = useState<any | null>(null);

  // ------ HANDLERS ------ //
  // ------ HANDLERS ------ //
  const handleEditProgram = (program: any) => {
    setEditProgramData(program);
    setShowFormModal(true);
  };

  const handleNewProgram = () => {
    setEditProgramData(null);
    setShowFormModal(true);
  };

  const handleDeleteProgramModal = (id: number) => {
    setSelectedProgramId(id);
    setShowDeleteProgramModal(true);
  };

  const handleShowShowProgramModal = (id: number) => {
    setSelectedProgramId(id);
    setShowShowProgramModal(true);
  };



  const menuItems = useMemo(() => {
    return [
      ...(id_user === "1" ? ["ID Program"] : []),
      "Program Date",
      "Client",
      "Departure",
      "Destination",
      "Type",
      "Driver",
      "Vehicle",
      "Observation",
      "Status",
    ];
  }, [id_user]);

  const columnsMap = {
    ...(id_user === "1" && { id_program: "ID" }),
    program_date: "Program Date",
    client_name: "Client",
    departure: "Departure",
    destination: "Destination",
    type: "Type",
    nom_conducteur: "Driver",
    immatriculation_vehicule: "Vehicle",
    observation: "Observation",
    status: "Status",
  };
  const [programs, setPrograms] = useState<ProgramInterface[]>([]);

  const fetchPrograms = async (pageOverride?: number) => {
    try {
      setLoading(true);
      const pageToUse = pageOverride ?? currentPage;
      if (pageOverride) setCurrentPage(pageToUse);

      // Requête principale pour récupérer les programmes
      const res = await fetch(`${backendUrl}/api/program/search`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_user,
          limitValue: limit,
          currentPage: pageToUse,
          id_site,
          search: searchValue,// champ sur lequel filtrer
          type,
          column,
          sort,
          filters: {
            program_date: filterDate,
          },
        }),
      });

      if (!res.ok) throw new Error("Failed to fetch programs");
      const data = await res.json();

      // Requête pour récupérer le total filtré côté serveur
      const totalRes = await fetch(`${backendUrl}/api/program/totalpage`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_user,
          search: searchValue,
          type,
          id_site,
          filters: {
            program_date: filterDate,
          },
        }),
      });

      if (!totalRes.ok) throw new Error("Failed total count");
      const totalData = await totalRes.json();
      const totalCount = totalData[0]?.count || 0;

      setPrograms(Array.isArray(data) ? data : []);
      setTotal(totalCount);
      setPageCount(Math.ceil(totalCount / limit));
    } catch (e) {
      console.error(e);
      setPrograms([]);
      setTotal(0);
      setPageCount(0);
    } finally {
      setLoading(false);
    }
  };



  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLimit = parseInt(e.target.value);
    setLimit(newLimit);
    setCurrentPage(1); // revenir à la première page
    fetchPrograms(1); // relancer fetch avec la nouvelle limite
  };



  // ------------------------
  // RENDER
  // ------------------------
  return (
    <Offcanvas show={show} onHide={handleModalHide}  placement="end" className="w-75 w-lg-75">
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          {isEditMode ? (
            <span style={{ color: "#28a745", fontWeight: "600" }}>Update Program</span>
          ) : (
            <span style={{ color: "#007bff", fontWeight: "600" }}>New Program</span>
          )}
        </Offcanvas.Title>
      </Offcanvas.Header>

      <Form onSubmit={handleSubmit}>
        <Offcanvas.Body>
          <div className="row g-3">


            
            {/* Client */}
            <div className="col-12 col-md-4">
              <SelectGroup
                controlId="id_client"
                name="id_client"
                label={translate("Site")}
                icon="user"
                options={clients.map((c) => ({ value: String(c.id_client), label: c.client_name }))}
                valueType={{
                  value: formData.id_client || "",
                  label:
                    clients.find((c) => String(c.id_client) === formData.id_client)?.client_name ||
                    translate("Select Client"),
                }}
                onChange={(selected) =>
                  setFormData((prev) => ({
                    ...prev,
                    id_client: String(selected.value),
                  }))
                }
              />
            </div>

         

            {/* Client */}
            <div className="col-12 col-md-4">
              <SelectGroup
                controlId="id_client"
                name="id_client"
                label={translate("Client")}
                icon="user"
                options={clients.map((c) => ({ value: String(c.id_client), label: c.client_name }))}
                valueType={{
                  value: formData.id_client || "",
                  label:
                    clients.find((c) => String(c.id_client) === formData.id_client)?.client_name ||
                    translate("Select Client"),
                }}
                onChange={(selected) =>
                  setFormData((prev) => ({
                    ...prev,
                    id_client: String(selected.value),
                  }))
                }
              />
            </div>


               {/* Program Date */}
            <div className="col-12 col-md-4">
              <Form.Group controlId="program_date">
                <Form.Label>{translate("Program Date")}</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.program_date}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, program_date: e.target.value }))
                  }
                />
              </Form.Group>
            </div>

            {/* Departure */}
            <div className="col-12 col-md-4">
              <Form.Group controlId="id_departure">
                <Form.Label>{translate("Departure")}</Form.Label>
                <Form.Control
                  as="select"
                  value={formData.id_departure}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, id_departure: e.target.value }))
                  }
                >
                  <option value="">{translate("Select departure")}</option>
                  {destinations.map((dest) => (
                    <option key={dest.id_destination} value={dest.id_destination.toString()}>
                      {dest.name}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </div>

            {/* Destination */}
            <div className="col-12 col-md-4">
              <Form.Group controlId="destination_ids">
                <Form.Label>{translate("Destination")}</Form.Label>

                <div className="border rounded p-2" style={{ minHeight: "38px" }}>
                  <div className="d-flex flex-wrap align-items-center">
                    {formData.destination_ids.map((destId, index) => {
                      const destination = destinations.find(
                        (d) => d.id_destination.toString() === destId
                      );
                      return (
                        <span
                          key={destId}
                          className="badge bg-primary me-1 mb-1 p-2 d-inline-flex align-items-center"
                        >
                          {destination?.name}
                          <button
                            type="button"
                            className="btn-close btn-close-white ms-1"
                            style={{ fontSize: "0.7rem" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setFormData((prev) => ({
                                ...prev,
                                destination_ids: prev.destination_ids.filter(
                                  (_, i) => i !== index
                                ),
                              }));
                            }}
                          ></button>
                        </span>
                      );
                    })}

                    {/* Search input */}
                    <input
                      type="text"
                      className="border-0 flex-grow-1"
                      placeholder={
                        formData.destination_ids.length === 0
                          ? translate("Search destinations...")
                          : ""
                      }
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
                      onKeyDown={(e) => {
                        if (e.key === "Backspace" && e.currentTarget.value === "") {
                          setFormData((prev) => ({
                            ...prev,
                            destination_ids: prev.destination_ids.slice(0, -1),
                          }));
                        }
                      }}
                      style={{ minWidth: "100px", outline: "none" }}
                    />
                  </div>
                </div>

                {/* Dropdown */}
                <div className="dropdown">
                  <div
                    className="dropdown-menu w-100 show"
                    style={{
                      maxHeight: "200px",
                      overflowY: "auto",
                      display: searchTerm ? "block" : "none",
                    }}
                  >
                    {destinations
                      .filter(
                        (dest) =>
                          !formData.destination_ids.includes(dest.id_destination.toString()) &&
                          dest.name.toLowerCase().includes(searchTerm.toLowerCase())
                      )
                      .map((dest) => (
                        <button
                          type="button"
                          key={dest.id_destination}
                          className="dropdown-item"
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              destination_ids: [
                                ...prev.destination_ids,
                                dest.id_destination.toString(),
                              ],
                            }));
                            setSearchTerm("");
                          }}
                        >
                          {dest.name}
                        </button>
                      ))}

                    {destinations.filter(
                      (dest) =>
                        !formData.destination_ids.includes(dest.id_destination.toString()) &&
                        dest.name.toLowerCase().includes(searchTerm.toLowerCase())
                    ).length === 0 && searchTerm && (
                        <div className="dropdown-item text-muted">No destinations found</div>
                      )}
                  </div>
                </div>
              </Form.Group>
            </div>

            {/* Type */}
            <div className="col-12 col-md-4">
              <Form.Group controlId="type">
                <Form.Label>{translate("Type")}</Form.Label>
                <Form.Control
                  as="select"
                  value={formData.type}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, type: e.target.value }))
                  }
                >
                  <option value="">{translate("Select type")}</option>
                  {typeOptions.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </div>

            {/* Driver */}
            <div className="col-12 col-md-4">
              <SelectGroup
                controlId="id_conducteur"
                name="id_conducteur"
                label={translate("Driver")}
                icon="user-tie"
                options={drivers.map((d) => ({
                  value: String(d.id_conducteur),
                  label: `${d.prenom_conducteur} ${d.nom_conducteur}`,
                }))}
                valueType={{
                  value: formData.id_conducteur || "",
                  label:
                    drivers.find((d) => String(d.id_conducteur) === formData.id_conducteur)
                      ? `${drivers.find(
                        (d) => String(d.id_conducteur) === formData.id_conducteur
                      )?.prenom_conducteur} ${drivers.find(
                        (d) => String(d.id_conducteur) === formData.id_conducteur
                      )?.nom_conducteur
                      }`
                      : translate("Select Driver"),
                }}
                onChange={(selected) =>
                  setFormData((prev) => ({
                    ...prev,
                    id_conducteur: String(selected.value),
                  }))
                }
              />
            </div>

            {/* Vehicle */}
            <div className="col-12 col-md-4">
              <SelectGroup
                controlId="id_vehicle"
                name="id_vehicle"
                label={translate("Vehicle")}
                icon="truck"
                options={vehicles.map((v) => ({
                  value: String(v.id_vehicule),
                  label: v.immatriculation_vehicule || `#${v.id_vehicule}`,
                }))}
                valueType={{
                  value: formData.id_vehicle || "",
                  label:
                    vehicles.find((v) => String(v.id_vehicule) === formData.id_vehicle)
                      ?.immatriculation_vehicule || translate("Select Vehicle"),
                }}
                onChange={(selected) =>
                  setFormData((prev) => ({
                    ...prev,
                    id_vehicle: String(selected.value),
                  }))
                }
              />
            </div>

            {/* Status */}
            <div className="col-12 col-md-4">
              <Form.Group controlId="status">
                <Form.Label>{translate("Status")}</Form.Label>
                <Form.Control
                  as="select"
                  value={formData.status}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, status: e.target.value }))
                  }
                >
                  <option value="">{translate("Select status")}</option>
                  {statusOptions.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </div>

            {/* Observation */}
            <div className="col-12 col-md-6">
              <Form.Group controlId="observation">
                <Form.Label>{translate("Observation")}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={formData.observation}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, observation: e.target.value }))
                  }
                />
              </Form.Group>
            </div>
              {/* Observation2 */}
            <div className="col-12 col-md-6">
              <Form.Group controlId="observation2">
                <Form.Label>{translate("Observation 2")}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={formData.observation}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, observation: e.target.value }))
                  }
                />
              </Form.Group>
            </div>
            
          </div>

          <ProgramTableModal
            loading={loading}
            programs={programs}
            id_site={siteId}
            selectedColumns={selectedColumns}
            copiedId={copiedId}
            copyToClipboard={copyToClipboard}
            translate={(t: string) => t}
            sortColumn={column}
            sortOrder={sort}
            handleSortingColumn={(col) => {
              setColumn(col);
              setSort(sort === "ASC" ? "DESC" : "ASC");
            }}
            handleShowNewDeadlineModal={() => { }}
            handleShowShowProgramModal={handleShowShowProgramModal}
            handleDeleteProgramModal={handleDeleteProgramModal}
            handleEditProgramModal={handleEditProgram}
            getStatusIcon={(status) => <span>{status}</span>}
            initialColumns={selectedColumns}
            limit={limit}
            total={total}
            pageCount={pageCount}
            currentPage={currentPage}
            handleSelectChange={handleSelectChange}
            handlePageClick={(data) => fetchPrograms(data.selected + 1)}
            columnsMap={columnsMap}
            handleColumnChange={(col) =>
              setSelectedColumns((prev) => ({ ...prev, [col]: !prev[col] }))
            }
            menuItems={menuItems}
            handleTypeSearch={handleTypeSearch}
            search={searchValue}
            handleAdvancedSearch={(e) => setSearchValue(e.target.value)}
            filterDate={filterDate}
            onFilterDateChange={setFilterDate}
            fetchPrograms={fetchPrograms}
          />

        </Offcanvas.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleModalHide}>
            {translate("Close")}
          </Button>

          {isEditMode && (
            <Button variant="outline-secondary" onClick={exitEditMode}>
              {translate("Switch to Add")}
            </Button>
          )}

          <Button variant={isEditMode ? "success" : "primary"} type="submit">
            {isEditMode ? translate("Update Program") : translate("Add Program")}
          </Button>
        </Modal.Footer>
      </Form>
    </Offcanvas>
  );
};

export default ProgramFormModal;
